import React, { useState, useEffect } from 'react';

const WhatsNextModal = ({ isOpen, onClose, orderNumber, addonCredits = 0, onCreditsUpdated }) => {
  // Add custom animations via style tag
  useEffect(() => {
    const styleTag = document.createElement('style');
    styleTag.innerHTML = `
      @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
      }
      @keyframes scaleIn {
        from { transform: scale(0); }
        to { transform: scale(1); }
      }
    `;
    document.head.appendChild(styleTag);
    return () => document.head.removeChild(styleTag);
  }, []);
  
  const [showCreditAnimation, setShowCreditAnimation] = useState(false);
  const [animatedCredits, setAnimatedCredits] = useState(0);
  const [animationStarted, setAnimationStarted] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setShowCreditAnimation(false);
      setAnimatedCredits(0);
      setAnimationStarted(false);
    }
  }, [isOpen]);

  const handleClose = () => {
    // Prevent multiple calls
    if (animationStarted) {
      return;
    }
    
    if (addonCredits > 0) {
      setAnimationStarted(true);
      // Show credit animation before closing
      setShowCreditAnimation(true);
      
      // Animate the credit count
      const duration = 2000; // 2 seconds
      const steps = 50;
      const increment = addonCredits / steps;
      let current = 0;
      
      const interval = setInterval(() => {
        current += increment;
        if (current >= addonCredits) {
          setAnimatedCredits(addonCredits);
          clearInterval(interval);
          
          // Close after animation completes
          setTimeout(() => {
            // Refresh user credits BEFORE closing modal
            if (onCreditsUpdated) {
              onCreditsUpdated();
            }
            onClose();
            setAnimationStarted(false);
          }, 1000);
        } else {
          setAnimatedCredits(Math.floor(current));
        }
      }, duration / steps);
    } else {
      onClose();
    }
  };

  if (!isOpen) return null;

  // If showing credit animation, show that overlay
  if (showCreditAnimation) {
    return (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[1001] p-4">
        <div className="text-center animate-[fadeIn_0.3s_ease-in]">
          <div className="mb-6">
            <div className="w-32 h-32 mx-auto bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center shadow-2xl animate-[scaleIn_0.5s_ease-out]">
              <svg className="w-16 h-16 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
          <div className="text-white">
            <h3 className="text-4xl font-bold mb-3">
              +{animatedCredits} Credits Added!
            </h3>
            <p className="text-xl text-gray-200">
              Animation credits have been added to your account
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[1000] p-4">
      <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="bg-gradient-to-r from-green-500 to-emerald-600 p-6 text-white sticky top-0 z-10">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                <svg className="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <div>
                <h2 className="text-3xl font-bold">
                  Order Confirmed!
                </h2>
                {orderNumber && (
                  <p className="text-green-100 text-sm mt-1">
                    Order {orderNumber}
                  </p>
                )}
              </div>
            </div>
            <button 
              onClick={handleClose}
              className="text-white hover:bg-white/20 rounded-full p-2 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="p-8">
          {/* Success Message */}
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-10 h-10 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">Thank You for Your Order!</h3>
            <p className="text-gray-600">
              A shipping kit with a prepaid label and detailed instructions will be sent to your address.
            </p>
          </div>

          {/* What's Next Steps */}
          <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl p-6 mb-6">
            <h4 className="font-bold text-xl mb-6 text-gray-800">
              What to Do Next
            </h4>

            <div className="space-y-6">
              {/* Step 1 */}
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                    </svg>
                  </div>
                </div>
                <div>
                  <h5 className="font-semibold text-gray-900 mb-2">Receive Your Shipping Kit</h5>
                  <p className="text-sm text-gray-600 mb-2">
                    We're shipping you a complete shipping kit with protective packaging materials and a prepaid return shipping label. Track your kit delivery in the "My Orders" tab.
                  </p>
                  <p className="text-sm font-semibold text-purple-600">
                    Expected Arrival: 3-5 business days
                  </p>
                </div>
              </div>

              {/* Step 2 */}
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                </div>
                <div>
                  <h5 className="font-semibold text-gray-900 mb-2">Pack Your Photos Safely</h5>
                  <p className="text-sm text-gray-600 mb-2">
                    Use the materials in the kit to safely pack your photos. We accept:
                  </p>
                  <ul className="text-sm text-gray-600 space-y-1 ml-4">
                    <li className="flex items-start">
                      <span className="text-green-500 mr-2">✓</span>
                      Standard prints (4×6, 5×7, 8×10)
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-500 mr-2">✓</span>
                      Polaroids and instant photos
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-500 mr-2">✓</span>
                      Vintage photographs
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-500 mr-2">✓</span>
                      Photos from albums
                    </li>
                  </ul>
                </div>
              </div>

              {/* Step 3 */}
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                    </svg>
                  </div>
                </div>
                <div>
                  <h5 className="font-semibold text-gray-900 mb-2">Ship Your Photos Back</h5>
                  <p className="text-sm text-gray-600 mb-2">
                    Use the prepaid shipping label included in your kit. Drop off at any shipping location or schedule a free pickup.
                  </p>
                  <p className="text-sm font-semibold text-green-600">
                    ✓ Fully insured • ✓ Signature required • ✓ Tracking included
                  </p>
                </div>
              </div>

              {/* Step 4 */}
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                </div>
                <div>
                  <h5 className="font-semibold text-gray-900 mb-2">Track Your Order</h5>
                  <p className="text-sm text-gray-600">
                    Monitor your kit delivery and return shipment status in the "My Orders" tab. We'll notify you when we receive your photos and when digitization is complete (7-10 business days after receipt).
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* 60-Day Warning */}
          <div className="bg-yellow-50 border-2 border-yellow-300 rounded-xl p-5 mb-6">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0">
                <svg className="w-6 h-6 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                </svg>
              </div>
              <div>
                <h4 className="font-bold text-yellow-900 mb-2">⏰ 60-Day Return Window</h4>
                <p className="text-sm text-yellow-800 leading-relaxed">
                  Your prepaid return shipping label <strong>must be used within 60 days</strong> of receiving your kit. After 60 days, the label expires and your order may be canceled. <strong>Shipping costs are non-refundable.</strong>
                </p>
              </div>
            </div>
          </div>

          {/* Important Notes */}
          <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-6 mb-6">
            <h4 className="font-bold text-amber-900 mb-3 flex items-center">
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              Important Reminders
            </h4>
            <ul className="text-sm text-amber-900 space-y-2">
              <li className="flex items-start">
                <span className="mr-2">•</span>
                Your shipping kit will arrive in 3-5 business days with tracking
              </li>
              <li className="flex items-start">
                <span className="mr-2">•</span>
                Use the provided materials to pack photos securely
              </li>
              <li className="flex items-start">
                <span className="mr-2">•</span>
                Include the order slip with your order number {orderNumber ? `(${orderNumber})` : ''} inside the package
              </li>
              <li className="flex items-start">
                <span className="mr-2">•</span>
                Processing begins within 24 hours of receiving your photos
              </li>
              <li className="flex items-start">
                <span className="mr-2">•</span>
                Digitization time: 7-10 business days after we receive your package
              </li>
              <li className="flex items-start">
                <span className="mr-2">•</span>
                Your originals will be carefully returned to you once complete
              </li>
            </ul>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4">
            <button
              onClick={handleClose}
              className="flex-1 bg-gradient-to-r from-purple-600 to-blue-600 text-white py-3 rounded-xl hover:opacity-90 transition-opacity font-bold flex items-center justify-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              View My Orders
            </button>
          </div>

          <p className="text-xs text-center text-gray-500 mt-4">
            Questions? Contact us at support@magic-moments.ai
          </p>
        </div>
      </div>
    </div>
  );
};

export default WhatsNextModal;
